/**
 * FoodPulseContributor — public author profile.
 *
 * URL: /food-pulse/contributors/:slug
 *
 * Renders a named byline page (dietitian / food-safety specialist /
 * farmer / editorial team) with credentials, bio, expertise areas,
 * the guides they've reviewed, and a corrections log. Every card that
 * shows a byline links here so readers can verify who reviewed the
 * copy they're reading.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, BadgeCheck, BookOpen, Loader2, User, Sprout, ShieldCheck, Utensils,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const ROLE_META = {
  "dietitian":               { label: "Registered Dietitian",     Icon: Utensils,   tint: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  "food-safety-specialist":  { label: "Food Safety Specialist",   Icon: ShieldCheck, tint: "bg-sky-50 text-sky-800 ring-sky-200" },
  "farmer":                  { label: "Working Farmer",           Icon: Sprout,     tint: "bg-amber-50 text-amber-800 ring-amber-200" },
  "editor":                  { label: "FoodPulse Editorial Team", Icon: BookOpen,   tint: "bg-slate-100 text-slate-700 ring-slate-200" },
};

export default function FoodPulseContributor() {
  const { slug } = useParams();
  const [contributor, setContributor] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContributor(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await http.get(`/food-pulse/contributors/${slug}`);
        if (!cancelled) setContributor(r.data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <FoodPulseTopNav />
        <FoodPulseJourneyNav />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="fp-contributor-not-found">
          <User className="mx-auto h-8 w-8 text-slate-300" />
          <h1 className="mt-3 font-serif text-2xl text-slate-900">Contributor not found</h1>
          <p className="mt-2 text-sm text-slate-600">
            This byline may have been retired.
          </p>
          <Link
            to="/food-pulse/contributors"
            className="mt-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <ArrowLeft className="h-3 w-3" /> All contributors
          </Link>
        </div>
      </div>
    );
  }

  if (!contributor) {
    return (
      <div className="min-h-screen bg-white">
        <FoodPulseTopNav />
        <FoodPulseJourneyNav />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-slate-500">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading profile…
        </div>
      </div>
    );
  }

  const meta = ROLE_META[contributor.role] || ROLE_META.editor;
  const { Icon } = meta;
  const crumb = [
    { name: "FoodPulse",    url: "https://foodpulse.net/food-pulse" },
    { name: "Contributors", url: "https://foodpulse.net/food-pulse/contributors" },
    { name: contributor.name, url: `https://foodpulse.net/food-pulse/contributors/${contributor.slug}` },
  ];
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: contributor.name,
    jobTitle: meta.label,
    description: contributor.bio,
    url: `https://foodpulse.net/food-pulse/contributors/${contributor.slug}`,
    knowsAbout: contributor.expertise_areas || [],
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`fp-contributor-${contributor.slug}`}>
      <SeoHead
        section="food-pulse"
        title={`${contributor.name} — FoodPulse Contributor`}
        description={contributor.bio.slice(0, 220)}
      />
      <JsonLd data={breadcrumbSchema(crumb)} />
      <JsonLd data={personSchema} />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav />

      <article className="mx-auto max-w-3xl px-4 py-10">
        <Link
          to="/food-pulse/contributors"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:underline"
          data-testid="fp-contributor-back"
        >
          <ArrowLeft className="h-3 w-3" /> All contributors
        </Link>

        <header className="mt-4 flex items-start gap-4">
          <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ring-1 ${meta.tint}`}>
            {contributor.headshot_url ? (
              <img
                src={contributor.headshot_url}
                alt={contributor.name}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <Icon className="h-7 w-7" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-500">
              <BadgeCheck className="h-3 w-3" /> {meta.label}
            </div>
            <h1 className="mt-1 font-serif text-3xl text-slate-900 sm:text-4xl">
              {contributor.name}
            </h1>
            <p className="mt-2 text-xs text-slate-600 sm:text-sm">
              {contributor.credentials}
            </p>
          </div>
        </header>

        {contributor.bio && (
          <div className="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-700">
            {contributor.bio}
          </div>
        )}

        {contributor.expertise_areas?.length > 0 && (
          <section className="mt-8" data-testid="fp-contributor-expertise">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Expertise
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {contributor.expertise_areas.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200"
                >
                  {e}
                </span>
              ))}
            </div>
          </section>
        )}

        {contributor.reviewed_topics?.length > 0 && (
          <section className="mt-8" data-testid="fp-contributor-guides">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Reviewed guides
            </div>
            <ul className="mt-2 space-y-1">
              {contributor.reviewed_topics.map((g) => (
                <li key={g}>
                  <Link
                    to={`/food-pulse/guides/${g}`}
                    className="text-sm text-emerald-700 hover:underline"
                    data-testid={`fp-contributor-reviewed-${g}`}
                  >
                    → {g.replace(/-/g, " ")}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {contributor.corrections?.length > 0 && (
          <section className="mt-8 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="fp-contributor-corrections">
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-800">
              Corrections log
            </div>
            <ul className="mt-2 space-y-1 text-xs text-amber-900">
              {contributor.corrections.map((c, i) => (
                <li key={i}>
                  <b>{c.date}</b> — {c.note}
                  {c.guide_slug && (
                    <>
                      {" · "}
                      <Link
                        to={`/food-pulse/guides/${c.guide_slug}`}
                        className="underline"
                      >
                        {c.guide_slug}
                      </Link>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}
